import React, { lazy, Suspense } from 'react'
import { QueryClient, QueryClientProvider } from 'react-query'
import ErrorBoundary from '@fs/zion-error-boundary'
import { targetEnv } from '@fs/zion-config'
import { AuthRoute, NotFound, Route, Switch, useLocation } from '@fs/zion-router'
import { NoticeLoading } from '@fs/zion-icon'
import { BrightspotRouting } from '@fs/zion-cms'
import { useFeatureFlag } from '@fs/zion-flags'
import NavigationTabs from './components/shared/SubNavigationBar'
import useNavLinks from './components/hooks/useNavLinks'
import QueuePermissionsProvider from './components/hooks/QueuePermissionsProvider'
import CanAddPersonalDataProvider from './components/hooks/CanAddPersonalDataProvider'

// Dynamically load components to reduce bundle size
// https://reactjs.org/docs/react-api.html#reactlazy
const HelperResourcesPage = lazy(() => import('./components/consultant/helper/HelperResourcesPage'))
const FamilyHistoryNetworkPage = lazy(
  () => import('./components/consultant/family-history-network/FamilyHistoryNetworkPage')
)
const PlannerPage = lazy(() => import('./components/planner/PlannerPage'))
const ProfileOptInPage = lazy(() => import('./components/directory/ProfileOptInPage'))
const OptInPage = lazy(() => import('./components/planner/OptInPage'))
const HelpAndLearningPage = lazy(() => import('./components/helpcenter/learning/HelpAndLearningPage'))
const HelpCenterPage = lazy(() => import('./components/helpcenter/landing-pages/help-center/HelpCenterPage'))
const LearningCenterPage = lazy(
  () => import('./components/helpcenter/landing-pages/learning-center/LearningCenterPage')
)
const OneOnOnePage = lazy(() => import('./components/helpcenter/one-on-one/OneOnOnePage'))
const ClassesAndVideosPage = lazy(() => import('./components/helpcenter/classes-and-videos/ClassesAndVideosPage'))
const CommunityPage = lazy(() => import('./components/helpcenter/community/CommunityPage'))
const DevRouter = React.lazy(() => import('./components/dev/DevRouter'))
const ContactUsPage = React.lazy(() => import('./components/helpcenter/contact-us/ContactUsPage'))
const ContactUsRedirect = React.lazy(() => import('./components/helpcenter/contact-us/ContactUsRedirect'))
const AssistantPage = lazy(() => import('./components/assistant/AssistantPage'))
const isProd = targetEnv === 'prod'

const queryClient = new QueryClient()

function App() {
  const { pathname } = useLocation()
  const navLinks = useNavLinks(pathname)
  const newHelpCenterFlag = useFeatureFlag('help_helpCenter_newLanding')
  const newLearningCenterFlag = useFeatureFlag('help_learningCenter_newLanding')
  const brightspotSiteFlag = useFeatureFlag('help_helpCenter_zionCms')
  const contactUsFlag = useFeatureFlag('help_contactUsPage')
  const assistantFlag = useFeatureFlag('help_fsa_single_page')
  return (
    <ErrorBoundary>
      <CanAddPersonalDataProvider>
        <QueryClientProvider client={queryClient}>
          <Suspense fallback={<NoticeLoading />}>
            <NavigationTabs navLinks={navLinks} />
            <Switch>
              <Route exact path="/" component={() => 'Root page placeholder'} />
              <Route exact path="/helper" requirePersonalData>
                <QueuePermissionsProvider>
                  <HelperResourcesPage />
                </QueuePermissionsProvider>
              </Route>
              <Route path="/helper/planner/workwith" requirePersonalData>
                <FamilyHistoryNetworkPage />
              </Route>
              <AuthRoute exact path="/helper/planner" requirePersonalData>
                <PlannerPage />
              </AuthRoute>
              <AuthRoute
                path={['/planner/link', '/planner/link/:verificationCode', '/helper/planner/link']}
                requirePersonalData
                exact
              >
                <OptInPage />
              </AuthRoute>
              <AuthRoute path="/helper/profile-opt-in" component={ProfileOptInPage} requirePersonalData />
              <Route path={['/helpcenter/community-landing', '/help/helpcenter/community-landing']} requirePersonalData>
                <CommunityPage />
              </Route>
              <Route path={['/helpcenter/one-on-one', '/help/helpcenter/one-on-one']} requirePersonalData>
                <OneOnOnePage />
              </Route>
              <Route path={['/helpcenter/classes-and-videos', '/help/helpcenter/classes-and-videos']}>
                <ClassesAndVideosPage />
              </Route>
              {brightspotSiteFlag.isOn && (
                <Route
                  path={[
                    '/help/helpcenter/article',
                    '/helpcenter/article',
                    '/help/helpcenter/family-tree',
                    '/helpcenter/family-tree',
                    '/help/helpcenter/search',
                    '/helpcenter/search',
                  ]}
                  render={() => {
                    return <BrightspotRouting site={brightspotSiteFlag.config} />
                  }}
                />
              )}
              <Route path={['/helpcenter/landing', '/help/helpcenter/landing', '/helpcenter', '/help/helpcenter']}>
                {newHelpCenterFlag.isOn ? <HelpCenterPage /> : <HelpAndLearningPage />}
              </Route>
              {newLearningCenterFlag.isOn && (
                <Route exact path={['/learningcenter', '/help/learningcenter']}>
                  <LearningCenterPage />
                </Route>
              )}
              {contactUsFlag.isOn && (
                <Route exact path={['/contact-us', '/help/contact-us']}>
                  <ContactUsRedirect />
                </Route>
              )}
              {contactUsFlag.isOn && (
                <Route path={['/contact-us/:region', '/help/contact-us/:region']}>
                  <ContactUsPage />
                </Route>
              )}
              {/* Internal Assistant testing page, gated behind a flag (off by default), reachable only by direct URL. */}
              {/* Registered as '/assistant'; resolves to the external '/help/assistant' under the app's '/help' basename (same convention as '/helper'). */}
              {assistantFlag.isOn && (
                <AuthRoute exact path="/assistant" requirePersonalData>
                  <AssistantPage />
                </AuthRoute>
              )}
              {/* Sample pages, hackathon projects, dev helpers, anything else that should be shown everywhere besides prod */}
              {isProd ? null : <Route path="/dev" component={DevRouter} />}
              {/* 404 fallback */}
              <Route component={NotFound} />
            </Switch>
          </Suspense>
        </QueryClientProvider>
      </CanAddPersonalDataProvider>
    </ErrorBoundary>
  )
}
export default App
